/**
 * Centralized cart actions exposed under `theme.cart`.
 *
 * Single source of truth for cart-mutation Ajax. Each action publishes
 * theme.pubsub.EVENTS.cartUpdate with the response payload so subscribers
 * (cart-drawer, cart-icon-bubble, etc.) update from a single fetch instead of
 * each issuing their own ?sections= GET.
 *
 * Trimmed 2026-05-02: addLine/changeLine/removeLine/updateCart/getCart had no
 * callers (cart-items.js and product-form.js still use direct fetch + their own
 * pubsub publish). Only updateNote remains — it's the silent-write code path
 * that benefits from being centralized (auto-disables section re-render so the
 * cart-page textarea doesn't lose focus on every keystroke). Re-add the rest
 * when there's a real consumer.
 *
 * Doc: docs/decisions/ADR-0003-pubsub-event-bus.md
 */

window.theme = window.theme || {};

theme.cart = (() => {
  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(async (r) => {
      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        const err = new Error(data.description || data.message || 'Cart request failed');
        err.status = r.status;
        err.data = data;
        throw err;
      }
      return data;
    });
  }

  /**
   * Updates the cart note. Silent by default (no `sections` re-render): the cart
   * page's note textarea is the only consumer of cart.note for visual state, and
   * re-rendering the cart page on every keystroke would clobber the textarea's
   * focus. Pubsub still fires so other subscribers (e.g. analytics) can react.
   *
   * @param {string} note
   * @returns {Promise<object>}
   */
  async function updateNote(note) {
    try {
      const data = await postJson(theme.routes.cartUpdate, { note });
      theme.pubsub.publish(theme.pubsub.EVENTS.cartUpdate, data);
      return data;
    } catch (err) {
      theme.pubsub.publish(theme.pubsub.EVENTS.cartError, err);
      throw err;
    }
  }

  return {
    updateNote,
  };
})();
